import * as React from 'react';

import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import SOVQueriesModalStepSearchTerms from './steps/search-terms/SOVQueriesModalStepSearchTerms';
import SOVQueriesModalStepSearchResults from './steps/search-results/SOVQueriesModalStepSearchResults';
import SOVQueriesModalStepRelatedChannels from './steps/related-channels/SOVQueriesModalStepRelatedChannels';

import { __t, applyTranslations } from 'utils/language';
import { EDIT } from 'modules/insights/sov/utils/sov';
import {
  SINGLE,
  STEP_RELATED_CHANNELS,
  STEP_SEARCH_RESULTS,
  STEP_SEARCH_TERMS
} from 'modules/insights/sov/utils/queries/sov-queries';

import type {
  SOVGeneralActionType,
  SOVGeneralModalTextFields,
  SOVQueryLocal,
  SOVQueryStep,
  SOVQueryStepType,
  SOVQueryType,
  SOVReport
} from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

type Props = {
  report: SOVReport,
  query: SOVQueryLocal,
  isMulti: boolean,
  actionType: SOVGeneralActionType,
  activeTab: string,
  stepIndex: number,
  stepCount: number,
  stepDirection: number,
  stepInfo: SOVQueryStep,
  textFields: SOVGeneralModalTextFields,
  onRenderActionButton: (isDisabled: boolean) => React.ReactNode,
  onRenderBackButton: () => React.ReactNode | undefined | null,
  onRenderCancelButton: () => React.ReactNode | undefined | null,
  onTextFieldChange: (fieldType: SOVQueryStepType, value: string) => void,
  onRadioButtonChange: (value: SOVQueryType) => void,
  onTabsSwitch: (identifier?: string | null) => void
};

type State = {
  isActionButtonDisabled: boolean
};

const MODAL_MSG_KEY = 'sov_queries_modal';

class SOVQueriesModalConsumerInsightsInstagram extends React.PureComponent<Props, State> {
  state = {
    isActionButtonDisabled: true
  };

  getStepType(): SOVQueryStepType {
    const { isMulti, stepInfo } = this.props;

    return isMulti ? stepInfo.stepType : SINGLE;
  }

  setActionButtonDisabledStatus = (status: boolean): void => {
    this.setState({
      isActionButtonDisabled: status
    });
  };

  renderStepSearchTerms = (): JSX.Element => {
    const {
      report,
      query,
      isMulti,
      actionType,
      textFields,
      onTextFieldChange,
      onRadioButtonChange
    } = this.props;

    return (
      <div className="sov-queries-modal-consumer-insights-instagram__search-terms">
        <SOVQueriesModalStepSearchTerms
          reportType={report.reportType}
          query={query}
          isMulti={isMulti}
          isEditMode={actionType === EDIT}
          textFields={textFields}
          onTextFieldChange={onTextFieldChange}
          onRadioButtonChange={onRadioButtonChange}
          onSetActionButtonDisabledStatus={this.setActionButtonDisabledStatus}
        />

      </div>
    );
  };

  renderStepSearchResults = (): JSX.Element => {
    const {
      report,
      query,
      isMulti,
      stepDirection,
      stepInfo,
      textFields,
      activeTab,
      onTabsSwitch
    } = this.props;

    return (
      <div className="sov-queries-modal-consumer-insights-instagram__search-results">
        <SOVQueriesModalStepSearchResults
          reportId={report.id}
          query={query}
          isMulti={isMulti}
          activeTab={activeTab}
          stepDirection={stepDirection}
          stepInfo={stepInfo}
          textFields={textFields}
          onTabsSwitch={onTabsSwitch}
          onSetActionButtonDisabledStatus={this.setActionButtonDisabledStatus}
        />
      </div>
    );
  };

  renderStepRelatedChannels = (): JSX.Element => {
    const { report, query, isMulti, stepDirection, stepInfo } = this.props;

    return (
      <div className="sov-queries-modal-consumer-insights-instagram__related-channels">
        <SOVQueriesModalStepRelatedChannels
          reportId={report.id}
          query={query}
          isMulti={isMulti}
          stepDirection={stepDirection}
          stepInfo={stepInfo}
          onSetActionButtonDisabledStatus={this.setActionButtonDisabledStatus}
        />
      </div>
    );
  };

  renderFooter(): React.ReactNode {
    const { onRenderActionButton, onRenderBackButton, onRenderCancelButton } = this.props;
    const { isActionButtonDisabled } = this.state;

    return (
      <ModalFrameFooter
        className="sov-queries-modal-consumer-insights-instagram__footer"
      >
        {onRenderCancelButton()}
        {onRenderBackButton()}
        {onRenderActionButton(isActionButtonDisabled)}
      </ModalFrameFooter>
    );
  }

  renderBody(): React.ReactNode {
    const { stepInfo } = this.props;

    const steps = {
      [STEP_SEARCH_TERMS]: this.renderStepSearchTerms,
      [STEP_SEARCH_RESULTS]: this.renderStepSearchResults,
      [STEP_RELATED_CHANNELS]: this.renderStepRelatedChannels
    };

    return (
      <ModalFrameInner
        minHeight={359}
      >
        {steps[stepInfo.key]()}
      </ModalFrameInner>
    );
  }

  render(): React.ReactNode {
    const { actionType, stepIndex, stepCount, stepInfo, textFields } = this.props;
    const stepType = this.getStepType();
    const showFor = stepInfo.isResultsStep;

    return (
      <ModalFrame
        title={__t(`${MODAL_MSG_KEY}.labels.${actionType}${showFor ? '_for' : ''}`, {
          searchTerm: textFields[stepType]
        })}
        classNameFrame="sov-queries-modal-consumer-insights-instagram"
        stepCounter={{
          index: stepIndex + 1,
          count: stepCount
        }}
      >
        {this.renderBody()}
        {this.renderFooter()}
      </ModalFrame>
    );
  }
}

export default SOVQueriesModalConsumerInsightsInstagram;

